import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ReportTemplateList } from '@futrli-sdk/predict';
import { FormattedMessage } from '@futrli/localisation';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyReportTemplateView } from '@futrli/views/views/reports/ModifyReportTemplateView/ModifyReportTemplateView';

import { useRouteIds } from '../../../hooks/useRouteIds';

interface DeleteTemplateDialogContainerProps {
  isOpen: boolean;
  onClose: () => void;
  template: ReportTemplateList;
}

export const DeleteTemplateDialogContainer = ({ isOpen, onClose, template }: DeleteTemplateDialogContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(() => new ModifyReportTemplateView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const { screen, handleAction: handleDelete, handleClose } = useDialogWizardState({
    isLoading: view.state.isDeletingTemplate,
    error: view.state.errorDeletingTemplate,
    onAction: () => view.deleteTemplate({ templateId: template.id }),
    onClose
  });

  const title = intl.formatMessage({ id: 'reporting.delete_template', defaultMessage: 'Delete template' });

  const deleteDescription = intl.formatMessage({
    id: 'reporting.confirm_delete_template_with_no_name',
    defaultMessage: 'Are you sure you want to delete this template?'
  });

  const thingsToKnow = [
    <FormattedMessage
      key="permanent"
      id="reporting.delete_template_things_to_know_permanent"
      defaultMessage="Deleting this template will remove it permanently from all non-guest workspaces"
    />,
    <FormattedMessage
      key="undone"
      id="reporting.delete_template_things_to_know_undone"
      defaultMessage="This cannot be undone"
    />,
    <FormattedMessage
      key="reports_unaffected"
      id="reporting.delete_template_things_to_know_reports"
      defaultMessage="Any report previously created from this template will be unaffected"
    />
  ];

  const loadingMessage = intl.formatMessage({
    id: 'reporting.deleting_template',
    defaultMessage: 'Deleting template...'
  });

  const successMessage = intl.formatMessage({
    id: 'reporting.template_deleted',
    defaultMessage: 'Template deleted'
  });

  const errorMessage = view.state.errorDeletingTemplate?.message;

  return (
    <DeleteDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={handleDelete}
      screen={screen}
      title={title}
      deleteDescription={deleteDescription}
      thingsToKnow={thingsToKnow}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      errorMessage={errorMessage}
      dataTestId="delete-template-dialog"
    />
  );
};
